import { DoubleSide, Group, Mesh, MeshBasicMaterial, NearestFilter, PlaneGeometry, SRGBColorSpace, TextureLoader } from '@anov/3d-core'
import type { SceneControl } from '@anov/3d-core'
import debounce from 'lodash/debounce'
import type { TextOption } from '../types'
import { createTextCanvas } from '../tools'
import ObjectLayer from '../../Base/ObjectLayer'

export const StrategyManage = {
  border: async () => (await import('./Border')).default,
  ellipse: async () => (await import('./Ellipse')).default,
}

const MIN_WIDTH = 1.2
export default class TitleMesh extends ObjectLayer {
  private _readyPromise: Promise<boolean>
  private _mesh: Group | Mesh
  private _listeners = new Set()

  _padding: [number, number]
  _bgUrl: string
  private _value: string
  private _fontSize: number
  private _color: string
  private _fontWeight: string
  private _offset: [number, number]
  private _uri: string
  private _debouncedUpdate: Function & { cancel: Function }

  constructor(option: TextOption, scencControl: SceneControl) {
    super(scencControl)
    const { value, fontSize, color, fontWeight, offset, bgUrl, uri } = option
    this._value = value
    this._fontSize = fontSize
    this._color = color
    this._fontWeight = fontWeight
    this._offset = offset
    if (bgUrl)
      this._bgUrl = bgUrl
    this._uri = uri
    this._debouncedUpdate = debounce(this.performUpdate.bind(this), 300)
  }

  public get ready() {
    return this._readyPromise
  }

  public get mesh() {
    return this._mesh
  }

  public get value(): string {
    return this._value
  }

  public set value(newValue: string) {
    if (this._value === newValue)
      return
    this._value = newValue
    this.update()
  }

  public get fontSize(): number {
    return this._fontSize
  }

  public set fontSize(newValue: number) {
    if (this._fontSize === newValue)
      return
    this._fontSize = newValue
    this.update()
  }

  public get color(): string {
    return this._color
  }

  public set color(newValue: string) {
    if (this._color === newValue)
      return
    this._color = newValue
    this.update()
  }

  public get fontWeight(): string {
    return this._fontWeight
  }

  public set fontWeight(newValue: string) {
    if (this._fontWeight === newValue)
      return
    this._fontWeight = newValue
    this.update()
  }

  public get offset(): [number, number] {
    return this._offset
  }

  public set offset(newValue: [number, number]) {
    this._offset = newValue
    this.update()
  }

  public get bgUrl(): string {
    return this._bgUrl
  }

  public set bgUrl(newValue: string) {
    if (this._bgUrl === newValue)
      return
    this._bgUrl = newValue
    this.update()
  }

  public get uri(): string {
    return this._uri
  }

  public set uri(newValue: string) {
    if (this._uri === newValue)
      return
    this._uri = newValue
    this.update()
  }

  // 注册监听
  public addReloadListener(callback) {
    this._listeners.add(callback)
  }

  // 移除监听
  public removeReloadListener(callback) {
    this._listeners.delete(callback)
  }

  public setupAnimation() {}

  // 销毁
  public destroy() {
    this._debouncedUpdate.cancel()
    this._listeners.clear()
    this._mesh.remove()
    this._mesh.removeFromParent()
  }

  init() {
    const { width: canvasW, height: canvasH, texture } = createTextCanvas({
      fontSize: this._fontSize,
      color: this._color,
      fontWeight: this._fontWeight,
      value: this._value,
    })
    // 转化为场景中的尺寸,并增加左右上下边距
    const width = Math.max(canvasW / 100 + this._padding[1] * 2, MIN_WIDTH)
    const height = canvasH / 100 + this._padding[0] * 2

    const group = new Group()
    this.initBackground(group, { width, height, source: this._bgUrl, offset: [0, 0, 0] })
    this.initText(group, { width: canvasW / 100, height: canvasH / 100, source: texture, offset: [0, 0, 0.001], color: this._color })
    this.add(group)
    this._mesh = group
  }

  // Update method with debounce
  private update(): void {
    this._debouncedUpdate()
  }

  private performUpdate(): void {
    this._mesh.remove()
    this._mesh.removeFromParent()
    this.init()
    this._listeners.forEach((cb: Function) => {
      cb()
    })
  }

  private initBackground(group: Group, option) {
    const mesh = this.createPanel(option)
    group.add(mesh)
  }

  private initText(group: Group, option) {
    const mesh = this.createPanel(option)
    group.add(mesh)
  }

  private createPanel({ width, height, source, color, offset }) {
    const texture = (typeof source == 'string') ? new TextureLoader().load(source) : source
    texture.colorSpace = SRGBColorSpace
    texture.magFilter = NearestFilter // 或 LinearFilter
    texture.minFilter = NearestFilter // 避免mipmap混合
    texture.generateMipmaps = false // 禁用mipmap生成

    const meshBasicMaterial = new MeshBasicMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.5,
      side: DoubleSide,
      color,
      depthWrite: true,
      depthTest: true,
      polygonOffset: true, // 解决深度冲突
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    })

    const mesh = new Mesh(
      new PlaneGeometry(width, height),
      meshBasicMaterial,
    )
    // mesh.renderOrder = 1000

    // this._scale && mesh.scale.fromArray(scale)
    offset && mesh.position.fromArray([...offset, 0])
    return mesh
  }
}